// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { isWeb } from '@/client/platform';
import { features } from '@/lib/edition';
import type { SettingsScope, SettingsSectionId } from '@/store/settingsStore';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Bot,
  Brain,
  Cable,
  FlaskConical,
  Link2,
  ShieldCheck,
  Server,
  TrendingUp,
  Cookie,
  CreditCard,
  Globe,
  Puzzle,
  Radio,
  Settings,
  StickyNote,
  SquareSlash,
  LineChart,
  Shrimp,
  Sparkles,
  WandSparkles,
} from 'lucide-react';

export interface SettingsNavigationItem {
  id: SettingsSectionId;
  labelKey: string;
  defaultLabel: string;
  icon: LucideIcon;
  /** Feature flag that must be on for the section to exist. */
  requires?:
    | 'billing'
    | 'usageDashboard'
    | 'gateway'
    | 'products'
    | 'vibeResearch'
    | 'vibeAstock'
    | 'notes'
    | 'slashCommands';
  /** The section needs the browser (Stripe returns there); hidden on desktop. */
  webOnly?: boolean;
}

export interface SettingsNavigationGroup {
  scope: SettingsScope;
  labelKey: string;
  defaultLabel: string;
  items: SettingsNavigationItem[];
}

export const SETTINGS_NAVIGATION: SettingsNavigationGroup[] = [
  {
    scope: 'global',
    labelKey: 'layout.global-setting',
    defaultLabel: 'Global Settings',
    items: [
      {
        id: 'models',
        labelKey: 'setting.models',
        defaultLabel: 'Models',
        icon: Sparkles,
      },
      {
        id: 'sub-agents',
        labelKey: 'setting.sub-agents',
        defaultLabel: 'Sub-agents',
        icon: Bot,
      },
      {
        id: 'skills',
        labelKey: 'agents.skills',
        defaultLabel: 'Skills',
        icon: WandSparkles,
      },
      {
        id: 'connectors',
        labelKey: 'layout.connectors',
        defaultLabel: 'Connectors',
        icon: Cable,
      },
      {
        id: 'channels',
        labelKey: 'layout.channels',
        defaultLabel: 'Channels',
        icon: Radio,
      },
      {
        id: 'memory',
        labelKey: 'agents.memory',
        defaultLabel: 'Memory',
        icon: Brain,
      },
      {
        id: 'gateway',
        labelKey: 'setting.gateway',
        defaultLabel: 'Gateway',
        icon: Shrimp,
        requires: 'gateway',
        webOnly: true,
      },
      {
        id: 'stock-intelligence',
        labelKey: 'setting.stock-intelligence',
        defaultLabel: 'Stock Intelligence',
        icon: LineChart,
        requires: 'products',
      },
      {
        id: 'daily-stock-analysis',
        labelKey: 'setting.daily-stock-analysis',
        defaultLabel: 'Daily Stock Analysis',
        icon: TrendingUp,
        requires: 'products',
      },
      {
        id: 'vibe-research',
        labelKey: 'setting.vibe-research',
        defaultLabel: 'Vibe Research',
        icon: FlaskConical,
        requires: 'vibeResearch',
      },
      {
        id: 'vibe-astock',
        labelKey: 'setting.vibe-astock',
        defaultLabel: 'Vibe AStock',
        icon: LineChart,
        requires: 'vibeAstock',
      },
      {
        id: 'services',
        labelKey: 'setting.services-title',
        defaultLabel: 'Background Services',
        icon: Server,
        requires: 'products',
      },
      {
        id: 'service-addresses',
        labelKey: 'setting.service-addresses',
        defaultLabel: 'Service Addresses',
        icon: Link2,
        requires: 'products',
      },
      {
        id: 'admin-admissions',
        labelKey: 'setting.admin-admissions',
        defaultLabel: 'Admissions Sources',
        icon: ShieldCheck,
        requires: 'products',
      },
      {
        id: 'admin-mail',
        labelKey: 'setting.admin-mail',
        defaultLabel: 'Email service',
        icon: ShieldCheck,
        requires: 'products',
      },
      {
        id: 'notes',
        labelKey: 'setting.notes',
        defaultLabel: 'Notes',
        icon: StickyNote,
        requires: 'notes',
      },
      {
        id: 'commands',
        labelKey: 'setting.commands',
        defaultLabel: 'Commands',
        icon: SquareSlash,
        requires: 'slashCommands',
      },
      {
        id: 'plugins',
        labelKey: 'setting.plugins',
        defaultLabel: 'Plugins',
        icon: Puzzle,
        requires: 'slashCommands',
      },
      {
        id: 'billing',
        labelKey: 'setting.billing',
        defaultLabel: 'Billing',
        icon: CreditCard,
        requires: 'billing',
        webOnly: true,
      },
      {
        id: 'usage',
        labelKey: 'setting.usage',
        defaultLabel: 'Usage',
        icon: BarChart3,
        requires: 'usageDashboard',
        webOnly: true,
      },
    ],
  },
  {
    scope: 'device',
    labelKey: 'layout.device',
    defaultLabel: 'Device',
    items: [
      {
        id: 'browser-connections',
        labelKey: 'layout.browser',
        defaultLabel: 'Browser',
        icon: Globe,
      },
      {
        id: 'browser-plugins',
        labelKey: 'layout.browser-extension',
        defaultLabel: 'Extension',
        icon: Puzzle,
      },
      {
        id: 'cookies',
        labelKey: 'layout.browser-cookie',
        defaultLabel: 'Cookies',
        icon: Cookie,
      },
    ],
  },
  {
    scope: 'settings',
    labelKey: 'layout.settings',
    defaultLabel: 'Settings',
    items: [
      {
        id: 'settings',
        labelKey: 'layout.settings',
        defaultLabel: 'Settings',
        icon: Settings,
      },
    ],
  },
];

/** Cloud-edition sections need their flag; billing ones also need the web. */
function isItemAvailable(item: SettingsNavigationItem): boolean {
  if (!item.requires) return true;
  return features[item.requires] && (!item.webOnly || isWeb());
}

export function visibleSettingsNavigation(): SettingsNavigationGroup[] {
  return SETTINGS_NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter(isItemAvailable),
  })).filter((group) => group.items.length > 0);
}

export function isSettingsSectionAvailable(section: SettingsSectionId): boolean {
  return visibleSettingsNavigation().some((group) =>
    group.items.some((item) => item.id === section)
  );
}

export function getSettingsNavigationItem(section: SettingsSectionId) {
  for (const group of SETTINGS_NAVIGATION) {
    const item = group.items.find((candidate) => candidate.id === section);
    if (item) return item;
  }
  return SETTINGS_NAVIGATION[0].items[0];
}
